import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Modal from '@mui/material/Modal'
import throttle from 'lodash/throttle'

import Api from '../../../../Api'
import { RootState } from '../../../../appReducer'
import { AccessAmplifySurveys, AccountTypes, Amplify } from '../../../../models/enums'
import { SearchBar } from '../../../../modules/shared/custom-fields/SearchBar'
import ManageLocationProducts from '../../../../modules/shared/ManageLocationProducts'
import { useAppDispatch } from '../../../../util/useAppDispatch'
import Paginator from '../../../shared/Paginator'
import {
    fetchPracticeProductsByPracticeId,
    savePracticeLocationProducts,
    savingPracticeLocationProducts,
} from '../../actions'
import { setLocationListMeta } from '../actions'
import { LocationCardProvider } from '../context/LocationCardContext'
import { openLocationAddEditModal } from '../location-forms/actions'
import {
    selectIsFetchingAllPracticeLocations,
    selectIsSavingPracticeLocationProducts,
    selectLocationListMetadata,
    selectPractice,
} from '../selectors/locationSelectors'

import LocationCard from './location-card/LocationCard'

interface Props {
    practiceId: Models.Practice['id']
    locations: Models.PracticeLocation[]
    products: Models.Product[]
    hasPayments: boolean
    hasDirectScheduling: boolean
    isListLoading: boolean
    practiceSearchTerms: Models.SearchTerms
}

const LocationList = ({ practiceId, locations, products, hasPayments, hasDirectScheduling, isListLoading }: Props) => {
    const dispatch = useAppDispatch()
    const account = useSelector((state: RootState) => state.app.self && state.app.self.account)
    const practice = useSelector((state: RootState) => selectPractice(state, practiceId))
    const enablePstPracticeCreation = useSelector(
        (state: RootState) => state.app.featureFlags?.enablePstPracticeCreation,
    )
    const isFetchingAllPracticeLocations = useSelector((state: RootState) =>
        selectIsFetchingAllPracticeLocations(state, practiceId),
    )
    const isSavingPracticeLocationProducts = useSelector(selectIsSavingPracticeLocationProducts)
    const { page, allPages, allRows } = useSelector((state: RootState) =>
        selectLocationListMetadata(state, practiceId),
    )

    const isCreatePracticeLocationEnabled = enablePstPracticeCreation !== false

    const [firstListLoadDone, setFirstListLoadDone] = useState<boolean>(false)
    const [searchKeyword, setSearchKeyword] = useState<string>('')
    const [hasSearch, setHasSearch] = useState<boolean>(false)
    const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false)
    const [locationIdForProducts, setLocationIdForProducts] = useState<string>('')

    const onCloseSettingsModal = () => {
        setShowSettingsModal(false)
        setLocationIdForProducts('')
    }

    const searchForLocations = useCallback(
        (searchKey: string) => {
            dispatch(setLocationListMeta(practiceId, { search: encodeURIComponent(searchKey), page: 1 }))
        },
        [dispatch, practiceId],
    )

    const throttledSearchLocations = useMemo(() => throttle(searchForLocations, 1000), [searchForLocations])

    useEffect(() => {
        throttledSearchLocations(searchKeyword.trim().toLocaleLowerCase())
    }, [searchKeyword, throttledSearchLocations])

    useEffect(() => {
        if ((allPages > 1 || searchKeyword.trim() !== '') && !firstListLoadDone) {
            setHasSearch(true)
            setFirstListLoadDone(true)
        }
    }, [allPages, searchKeyword, firstListLoadDone])

    const getSelectedLocation = (locationId: string): Models.PracticeLocation | undefined => {
        return locations.find(loc => loc.id === locationId)
    }

    const getCanEdit = (): boolean =>
        Boolean(
            account &&
                (account.type.id === AccountTypes.SimplifeyeStaff ||
                    account.type.id === AccountTypes.SuperAdmin ||
                    account.type.id === AccountTypes.PattersonStaff),
        )

    const getCanManageSurvey = (): boolean =>
        Boolean(
            products.some(product => product.value === Amplify.value && product.active === true) &&
                account?.hasAccess(AccessAmplifySurveys),
        )

    const searchByKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(e.target.value || '')
    }

    const clearSearch = () => {
        setSearchKeyword('')
    }

    const handlePageChange = (newPage: number) => {
        dispatch(setLocationListMeta(practiceId, { page: newPage }))
    }

    const handleOpenAddLocationModal = (): void => {
        dispatch(openLocationAddEditModal(null, practiceId))
    }

    const handleOpenLocationProductsModal = (locationId: string) => {
        setLocationIdForProducts(locationId)
        setShowSettingsModal(true)
    }

    const handleSaveLocationProducts = async (productsToSave: Array<Api.SaveProduct>) => {
        dispatch(savingPracticeLocationProducts(true))
        await dispatch(savePracticeLocationProducts(practiceId, locationIdForProducts, productsToSave))
        dispatch(savingPracticeLocationProducts(false))
        onCloseSettingsModal()
        if (practice) {
            dispatch(fetchPracticeProductsByPracticeId(practice.id))
        }
    }

    if ((page === 1 && !firstListLoadDone && !locations) || locations?.length > 8 || isFetchingAllPracticeLocations) {
        return (
            <div className="card-container">
                <div className="locations-tab__loader-container">
                    <div className="loader loader--global">
                        <CircularProgress
                            className="loader-spinner"
                            size={50}
                            color="primary"
                            variant="indeterminate"
                        />
                    </div>
                </div>
            </div>
        )
    }

    const isVyneGroup = practice?.vyneGroupId
    const searchPlaceholder = isVyneGroup
        ? 'Search by Practice (Customer) Name, Practice (Customer) ID, Simplifeye Location ID, or Facility ID'
        : 'Search by Location Name, Location ID, or Facility ID'

    return (
        <>
            {hasSearch && (
                <div className="search-bar-container">
                    <SearchBar
                        value={searchKeyword}
                        onChange={searchByKeyword}
                        onClear={clearSearch}
                        isMessageShown={searchKeyword.length > 0 && !locations.length}
                        noResultsMessage="No records found for given name or ID or Facility ID"
                        placeholder={searchPlaceholder}
                    />
                </div>
            )}
            <div className="card-container">
                <LocationCardProvider
                    practiceId={practiceId}
                    locations={locations}
                    hasPayments={hasPayments}
                    hasDirectScheduling={hasDirectScheduling}
                    isLocationEditable={getCanEdit()}
                    canManageSurvey={getCanManageSurvey()}
                    onOpenLocationProductsModal={handleOpenLocationProductsModal}
                >
                    {locations.map((location: Models.PracticeLocation) => (
                        <LocationCard key={`${location.name}-${location.id}`} location={location} />
                    ))}
                </LocationCardProvider>
                {isListLoading && (
                    <div className="locations-tab__loader-container">
                        <div className="loader loader--list">
                            <CircularProgress
                                className="loader-spinner"
                                size={50}
                                color="primary"
                                variant="indeterminate"
                            />
                        </div>
                    </div>
                )}
                {showSettingsModal && (
                    <Modal
                        className="pst-modal"
                        open={true}
                        onClose={(event, reason) => {
                            if (reason !== 'backdropClick') {
                                onCloseSettingsModal()
                            }
                        }}
                        disableEscapeKeyDown={true}
                    >
                        <div className="contents manage-products-modal">
                            <div className="close-modal-button" onClick={onCloseSettingsModal}>
                                <i className="material-icons">close</i>
                            </div>
                            <ManageLocationProducts
                                account={account}
                                practice={practice!}
                                location={getSelectedLocation(locationIdForProducts)}
                                onSave={handleSaveLocationProducts}
                                onClose={onCloseSettingsModal}
                                isSavingPracticeProducts={isSavingPracticeLocationProducts}
                            />
                        </div>
                    </Modal>
                )}
            </div>
            <div className="locations-tab__paginator-wrapper">
                <Paginator currentPage={page} paginationInfo={{ allRows, allPages }} selectPage={handlePageChange} />
            </div>
            {isCreatePracticeLocationEnabled && (
                <div className="locations-tab__footer" onClick={handleOpenAddLocationModal}>
                    <div className="button">
                        <i className="material-icons add-icon">add</i>
                        Add Location
                    </div>
                </div>
            )}
        </>
    )
}

export default LocationList
